import React, {useState} from "react";
import axios from "axios";
import Icon from "@mdi/react";
import {mdiClockOutline} from "@mdi/js";
import {OverlayTrigger, Popover, Button, Spinner} from "react-bootstrap";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";

/**
 * Formatiert einen Zeitstempel im deutschen Format, z. B.
 * "Stand: 15.07.2026, 14:15 Uhr". Ohne Wert wird ein sprechender
 * Platzhalter ausgegeben.
 *
 * @param {string|null|undefined} value - ISO-Zeitstempel oder leer.
 * @returns {string}
 */
function formatGermanTimestamp(value) {
    if (!value) {
        return "noch nie aktualisiert";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "noch nie aktualisiert";
    }
    const datePart = date.toLocaleDateString("de-DE", {day: "2-digit", month: "2-digit", year: "numeric"});
    const timePart = date.toLocaleTimeString("de-DE", {hour: "2-digit", minute: "2-digit"});
    return `Stand: ${datePart}, ${timePart} Uhr`;
}

/**
 * Sehr kleines Uhrensymbol neben einem Status-Badge, das per Klick ein
 * Popover mit dem genauen Aktualisierungs-Zeitpunkt dieser Spalte öffnet.
 * Über den Button im Popover lässt sich GENAU dieser eine Artikel sofort
 * neu von allen Quellen (Weclapp, B2B-Shop, B2C-Shop) abrufen; die Zeile
 * wird danach live mit dem Rückgabewert aktualisiert.
 *
 * @param {string|null} timestamp - Zeitstempel, auf den sich diese Spalte bezieht.
 * @param {string} label - Überschrift im Popover, z. B. "Stand des B2C-Shops".
 * @param {string} artikelnummer - Artikelnummer der zugehörigen Zeile.
 * @param {(article: object) => void} onRefreshed - Callback mit dem aktualisierten Artikel.
 */
export default function StatusTimestampControl({timestamp, label, artikelnummer, onRefreshed}) {
    const {addToast} = useExternalControls();
    const [loading, setLoading] = useState(false);
    const [show, setShow] = useState(false);

    const handleRefresh = async () => {
        setShow(false);
        setLoading(true);
        try {
            const {data} = await axios.post("/stock/overview/refresh-article", {artikelnummer});

            if (data?.article) {
                onRefreshed?.(data.article);
            }

            addToast(
                <FlexToast
                    title="Artikel aktualisiert"
                    autohide
                    message={`Artikel ${artikelnummer} wurde aktualisiert.`}
                />
            );

            if (Array.isArray(data?.warnings) && data.warnings.length > 0) {
                addToast(
                    <FlexToast
                        variant="warning"
                        title="Hinweise beim Aktualisieren"
                        autohide={false}
                        message={
                            <ul className="mb-0 ps-3">
                                {data.warnings.map((warning, index) => (
                                    <li key={index}>{warning}</li>
                                ))}
                            </ul>
                        }
                    />
                );
            }
        } catch (e) {
            const msg = e.response?.data?.message || e.message;
            addToast(
                <FlexToast
                    variant="danger"
                    title="Aktualisierung fehlgeschlagen"
                    autohide={false}
                    message={`Artikel ${artikelnummer} konnte nicht aktualisiert werden: ${msg}`}
                />
            );
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Spinner
                animation="border"
                role="status"
                className="ms-1 text-muted align-middle"
                style={{width: "0.7em", height: "0.7em", borderWidth: "1px"}}
            >
                <span className="visually-hidden">Wird aktualisiert…</span>
            </Spinner>
        );
    }

    return (
        <OverlayTrigger
            trigger="click"
            placement="top"
            flip
            rootClose
            show={show}
            onToggle={setShow}
            overlay={
                <Popover id={`status-timestamp-popover-${artikelnummer}-${label}`}>
                    <Popover.Header as="h6">{label}</Popover.Header>
                    <Popover.Body className="text-start" style={{minWidth: 240}}>
                        <div className="small mb-2">{formatGermanTimestamp(timestamp)}</div>
                        <Button size="sm" variant="outline-primary" onClick={handleRefresh}>
                            Diesen Artikel jetzt aktualisieren
                        </Button>
                    </Popover.Body>
                </Popover>
            }
        >
            <span
                role="button"
                tabIndex={0}
                aria-label={`${label} anzeigen`}
                className="ms-1 text-muted d-inline-flex align-items-center align-middle"
                style={{cursor: "pointer", fontSize: "0.7em"}}
            >
                <Icon path={mdiClockOutline} size={0.6}/>
            </span>
        </OverlayTrigger>
    );
}
